/**
 * ★★ Travas do fechamento (03/11/2026, itens 2, 3, 6 e 7) — as decisões da TELA de Remuneração.
 *
 * Puro: sem Firebase e sem React. A tela junta os dados; isto decide em que mês ela abre, se o
 * botão "Fechar o mês" aparece e o que a confirmação diz. Os números da folha continuam vindo de
 * `consolidarFechamento` (espelhado no servidor); nada aqui calcula dinheiro.
 */
import { chaveMesDe, mesDoRecebimento, mesPodeSerFechado, nomeDoMes } from '@/remuneracao/calculo';

/**
 * ★ O primeiro mês de folha deste produto: setembro/2026 foi o primeiro fechado em produção
 * (01/10/2026). Antes dele não há folha a fechar, e abrir a tela em agosto — mês com recebimento e
 * nunca fechado — seria abrir no mês errado.
 */
export const PRIMEIRO_MES_DA_FOLHA = '2026-09';

/** Os meses de `de` até `ate`, inclusive, em ordem ('AAAA-MM'). */
export function mesesEntre(de, ate) {
  const meses = [];
  let [a, m] = String(de).split('-').map(Number);
  const [aFim, mFim] = String(ate).split('-').map(Number);
  let voltas = 0;
  while ((a < aFim || (a === aFim && m <= mFim)) && voltas < 240) {
    meses.push(`${a}-${String(m).padStart(2, '0')}`);
    m += 1;
    if (m > 12) { m = 1; a += 1; }
    voltas += 1;
  }
  return meses;
}

/** O mês anterior a 'AAAA-MM'. */
export function mesAnterior(mes) {
  const [a, m] = String(mes).split('-').map(Number);
  return m === 1 ? `${a - 1}-12` : `${a}-${String(m - 1).padStart(2, '0')}`;
}

/**
 * ★★ Item 3 — os meses CANDIDATOS a abrir a tela, do mais antigo ao mais novo: do primeiro mês da
 * folha até o mês ANTERIOR ao atual (o atual ainda não pode ser fechado — item 2).
 */
export function mesesCandidatos(agora = new Date(), piso = PRIMEIRO_MES_DA_FOLHA) {
  const atual = chaveMesDe(agora);
  if (!atual) return [];
  const anterior = mesAnterior(atual);
  return anterior < piso ? [] : mesesEntre(piso, anterior);
}

/**
 * ★★ Item 3 — em que mês a tela abre: o mês ABERTO mais antigo que tenha movimento; sem nenhum,
 * o mês anterior ao atual.
 *
 * @param fechados      meses com `status: 'fechado'` (Set ou lista)
 * @param temMovimento  `(mes) => boolean` — já resolvido pela tela (recebimento ou comparecimento)
 */
export function escolherMesInicial({ agora = new Date(), fechados = [], temMovimento = () => false, piso = PRIMEIRO_MES_DA_FOLHA } = {}) {
  const fechadosSet = fechados instanceof Set ? fechados : new Set(fechados ?? []);
  for (const mes of mesesCandidatos(agora, piso)) {
    if (fechadosSet.has(mes)) continue;
    if (temMovimento(mes)) return mes;
  }
  return mesAnterior(chaveMesDe(agora));
}

/** ★ Item 3 — algum lead compareceu no mês? (pelo `compareceuEm`, calendário de São Paulo). */
export const mesesComComparecimento = (leads = []) =>
  new Set((leads ?? []).map((l) => chaveMesDe(l?.compareceuEm)).filter(Boolean));

const centavos = (v) => Math.round(Number(v || 0) * 100);

/**
 * ★★ Itens 2 e 6 — o botão "Fechar o mês" aparece? E, se não, por quê.
 *
 * ⚠ A trava de verdade é o servidor (`fecharMes` recusa mês corrente e futuro). Esconder o botão é
 * para ninguém tentar; o servidor é para ninguém conseguir.
 */
export function podeOferecerFechar({ mes, agora = new Date(), fechado = false, totais = null } = {}) {
  if (fechado) return { mostrar: false, motivo: null };
  if (!mesPodeSerFechado(mes, agora)) {
    return {
      mostrar: false,
      motivo: `${nomeDoMes(mes)} ainda não terminou. O fechamento fica disponível depois do último dia do mês.`,
    };
  }
  if (centavos(totais?.total) <= 0) {
    return { mostrar: false, motivo: 'Folha sem valor a pagar: não há o que fechar.' };
  }
  return { mostrar: true, motivo: null };
}

/** ★ Item 7 — quantos recebimentos são DO MÊS (a consulta traz um dia a mais de cada lado). */
export const lancamentosDoMes = (recebimentos = [], mes) =>
  (recebimentos ?? []).filter((r) => mesDoRecebimento(r) === mes).length;

const paraDataOuNull = (v) => {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** ★ Item 7 — a data da última importação CONFIRMADA (o histórico vem do mais novo ao mais velho). */
export function ultimaImportacaoConfirmada(importacoes = []) {
  const datas = (importacoes ?? [])
    .filter((i) => i?.status === 'confirmada')
    .map((i) => paraDataOuNull(i.confirmadaEm))
    .filter(Boolean)
    .sort((a, b) => b - a);
  return datas[0] ?? null;
}

const reais = (v) => Number(v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const dataHora = (d) => (d
  ? d.toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo', day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : 'nenhuma');

/** ★ Item 7 — o texto da confirmação do fechamento. */
export function textoDaConfirmacao({ mes, total, lancamentos, ultimaImportacao }) {
  return `Fechar ${nomeDoMes(mes)} com ${reais(total)} a pagar?\n\n`
    + `${lancamentos} lançamento${lancamentos === 1 ? '' : 's'} de recebimento no mês.\n`
    + `Última importação: ${dataHora(ultimaImportacao)}.\n\n`
    + 'Depois de fechado, corrigir um recebimento não muda o que foi pago.';
}

/** ★ Item 7 — o passo a passo curto que fica na tela. */
export const PASSO_A_PASSO = [
  'Importe a planilha do mês.',
  "Confira os 'Sem responsável'.",
  'Feche só depois do último dia.',
];
